import React, { useState, useEffect } from 'react'
import { MapPin, Square, Home, Zap, X, ChevronLeft, ChevronRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { propertyAPI } from '../services/api'
import useIntersectionObserver from '../hooks/useIntersectionObserver'

function PropertiesPage() {
  const [properties, setProperties] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedProperty, setSelectedProperty] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [heroVisible, setHeroVisible] = useState(false)

  // Animasyon için intersection observer'lar
  const [propertiesRef, propertiesVisible] = useIntersectionObserver()

  // Hero section için animasyon
  useEffect(() => {
    const timer = setTimeout(() => {
      setHeroVisible(true)
    }, 300)
    return () => clearTimeout(timer)
  }, [])

  // Backend'den ilanları getir
  useEffect(() => {
    loadProperties()
  }, [])

  const loadProperties = async () => {
    try {
      setLoading(true)
      const response = await propertyAPI.getAll()
      setProperties(response.data)
    } catch (err) {
      setError('İlanlar yüklenirken hata oluştu')
      console.error('Veri yükleme hatası:', err)
      setProperties([])
    } finally {
      setLoading(false)
    }
  }

  const PropertyCard = ({ property }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0)
    
    // İmaj dizisini oluştur ve URL'leri düzelt
    const processImages = (imgs) => {
      if (!imgs || imgs.length === 0) {
        return [property.image || 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=500']
      }
      return imgs.map(img => {
        if (typeof img === 'string') {
          // Eğer zaten tam URL ise olduğu gibi döndür
          if (img.startsWith('http')) return img
          // Backend'den gelen dosya adıysa tam URL'e çevir
          const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
          const backendUrl = isLocalhost 
            ? `http://localhost:5000` 
            : `https://organizem-backend.onrender.com`
          return `${backendUrl}/uploads/${img}`
        }
        return img
      })
    }
    
    const images = processImages(property.images)
    const currentImage = images[currentImageIndex]

    const nextImage = (e) => {
      e.stopPropagation()
      setCurrentImageIndex((prev) => (prev + 1) % images.length)
    }

    const prevImage = (e) => {
      e.stopPropagation()
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length)
    }

    const handleCardClick = () => {
      setSelectedProperty(property)
      setShowModal(true)
    }

    return (
      <div 
        onClick={handleCardClick}
        className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 cursor-pointer"
      >
        <div className="relative">
          <img 
            src={currentImage || 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=500'} 
            alt={property.title}
            className="w-full h-64 object-cover"
          />
          
          {/* Image Navigation */}
          {images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
              >
                <ChevronRight size={16} />
              </button>
              
              {/* Image indicators */}
              <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
                {images.map((image, index) => (
                  <div
                    key={`${property.id}-image-${index}`}
                    className={`w-2 h-2 rounded-full ${
                      index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </>
          )}

        <div className="absolute top-4 left-4">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            property.type === 'Satılık' 
              ? 'bg-green-100 text-green-800' 
              : 'bg-blue-100 text-blue-800'
          }`}>
            {property.type}
          </span>
        </div>
        {property.featured && (
          <div className="absolute top-4 right-4">
            <span className="bg-accent-500 text-white px-3 py-1 rounded-full text-sm font-medium">
              Öne Çıkan
            </span>
          </div>
        )}

      </div>
      
      <div className="p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          {property.title}
        </h3>
        <div className="flex items-center text-gray-600 mb-4">
          <MapPin size={16} className="mr-1" />
          <span className="text-sm">{property.location}</span>
        </div>
        
        <div className="space-y-2 mb-4 text-sm text-gray-600">
          <div className="flex items-center justify-between">
            <span className="flex items-center">
              <Home size={16} className="mr-2" />
              Kapalı Alan:
            </span>
            <span className="font-medium">{property.closedArea}m²</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="flex items-center">
              <Square size={16} className="mr-2" />
              Açık Alan:
            </span>
            <span className="font-medium">{property.openArea}m²</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="flex items-center">
              <Zap size={16} className="mr-2" />
              Elektrik Gücü:
            </span>
            <span className="font-medium">{property.electricPower}KW</span>
          </div>
        </div>
        
        {property.description && (
          <p className="text-sm text-gray-600 mb-4 line-clamp-2">
            {property.description}
          </p>
        )}
        
        <div className="flex items-center justify-between">
          <div>
            <span className="text-2xl font-bold text-primary-600">
              {parseInt(property.price).toLocaleString()} {property.currency || 'TL'}
            </span>
          </div>
          <div className="text-xs text-gray-500 italic">
            Detaylar için tıklayın
          </div>
        </div>
      </div>
    </div>
    )
  }

  const PropertyModal = () => {
    const [modalImageIndex, setModalImageIndex] = useState(0)
    
    if (!selectedProperty || !showModal) return null

    // Modal için resim işleme fonksiyonu
    const processModalImages = (imgs) => {
      if (!imgs || imgs.length === 0) {
        return [selectedProperty.image || 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=500']
      }
      return imgs.map(img => {
        if (typeof img === 'string') {
          if (img.startsWith('http')) return img
          const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
          const backendUrl = isLocalhost 
            ? `http://localhost:5000` 
            : `https://organizem-backend.onrender.com`
          return `${backendUrl}/uploads/${img}`
        }
        return img
      })
    }

    const modalImages = processModalImages(selectedProperty.images)
    const currentModalImage = modalImages[modalImageIndex]

    const nextModalImage = () => {
      setModalImageIndex((prev) => (prev + 1) % modalImages.length)
    }

    const prevModalImage = () => {
      setModalImageIndex((prev) => (prev - 1 + modalImages.length) % modalImages.length)
    }

    // Modal açıldığında resim indexini sıfırla
    React.useEffect(() => {
      setModalImageIndex(0)
    }, [selectedProperty])

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900">
              İlan Detayları
            </h3>
            <button
              onClick={() => setShowModal(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Modal Content */}
          <div className="p-6">
            {/* Property Image with Navigation */}
            <div className="relative mb-6">
              <img 
                src={currentModalImage} 
                alt={selectedProperty.title}
                className="w-full h-64 object-cover rounded-lg"
              />
              
              {/* Image Navigation for Modal */}
              {modalImages.length > 1 && (
                <>
                  <button
                    onClick={prevModalImage}
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    onClick={nextModalImage}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                  >
                    <ChevronRight size={20} />
                  </button>
                  
                  {/* Image indicators for Modal */}
                  <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
                    {modalImages.map((_, index) => (
                      <button
                        key={`modal-${selectedProperty.id}-image-${index}`}
                        onClick={() => setModalImageIndex(index)}
                        className={`w-3 h-3 rounded-full transition-colors ${
                          index === modalImageIndex ? 'bg-white' : 'bg-white/50 hover:bg-white/80'
                        }`}
                      />
                    ))}
                  </div>
                  
                  {/* Image counter */}
                  <div className="absolute top-2 right-2 bg-black/50 text-white px-2 py-1 rounded text-sm">
                    {modalImageIndex + 1} / {modalImages.length}
                  </div>
                </>
              )}

              <div className="absolute top-4 left-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  selectedProperty.type === 'Satılık' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-blue-100 text-blue-800'
                }`}>
                  {selectedProperty.type}
                </span>
              </div>
              {selectedProperty.featured && (
                <div className="absolute top-4 right-4">
                  <span className="bg-accent-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                    Öne Çıkan
                  </span>
                </div>
              )}
            </div>

            {/* Property Details */}
            <div className="space-y-4">
              <div>
                <h4 className="text-2xl font-bold text-gray-900 mb-2">
                  {selectedProperty.title}
                </h4>
                <div className="flex items-center text-gray-600 mb-4">
                  <MapPin size={16} className="mr-1" />
                  <span>{selectedProperty.location}</span>
                </div>
              </div>

              {/* Price */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-3xl font-bold text-primary-600">
                  {parseInt(selectedProperty.price).toLocaleString()} {selectedProperty.currency || 'TL'}
                </div>
              </div>

              {/* Technical Details */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-semibold text-gray-900 mb-3">Teknik Özellikler</h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center text-gray-600">
                      <Home size={16} className="mr-2" />
                      Kapalı Alan:
                    </span>
                    <span className="font-medium">{selectedProperty.closedArea}m²</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center text-gray-600">
                      <Square size={16} className="mr-2" />
                      Açık Alan:
                    </span>
                    <span className="font-medium">{selectedProperty.openArea}m²</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center text-gray-600">
                      <Zap size={16} className="mr-2" />
                      Elektrik Gücü:
                    </span>
                    <span className="font-medium">{selectedProperty.electricPower}KW</span>
                  </div>
                </div>
              </div>

              {/* Description */}
              {selectedProperty.description && (
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2">Açıklama</h5>
                  <p className="text-gray-600 leading-relaxed">
                    {selectedProperty.description}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex items-center justify-end gap-4 p-6 border-t border-gray-200">
            <button
              onClick={() => setShowModal(false)}
              className="btn-secondary"
            >
              Kapat
            </button>
            <Link to="/iletisim" className="btn-primary">
              İletişime Geç
            </Link>
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="pt-24 pb-12 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">İlanlar yükleniyor...</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-96 flex items-center text-white overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url("/foto/fab2.jpg")'
          }}
        ></div>
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/50"></div>
        
        {/* Content */}
        <div className="relative container-custom z-10">
          <div className="max-w-4xl">
          
          </div>
        </div>
      </section>

      <div ref={propertiesRef} className={`py-12 bg-gray-50 min-h-screen fade-in ${propertiesVisible ? 'visible' : ''}`}>
        <div className="container-custom">
          {error && (
            <div className="mb-8 p-4 bg-red-100 text-red-700 rounded-lg max-w-md mx-auto text-center">
              {error}
            </div>
          )}

          {/* Results Count */}
          <div className="mb-8">
            <p className="text-gray-600 text-center">
              <span className="font-semibold text-lg">{properties.length}</span> ilan listeleniyor
            </p>
          </div>

          {/* Properties Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {properties.map((property, index) => (
              <div
                key={property._id}
                className={`slide-up ${propertiesVisible ? 'visible' : ''}`}
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <PropertyCard property={property} />
              </div>
            ))}
          </div>

          {/* Property Modal */}
          <PropertyModal />
        </div>
      </div>
    </div>
  )
}

export default PropertiesPage